// ===========================================
// Newsletter API
// ===========================================
// API client for newsletter and blog features

import { apiClient } from '../services/api';
import {
  Newsletter,
  NewsletterListItem,
  NewsletterSection,
  PublicBlogPost,
  CreateNewsletterData,
  UpdateNewsletterData,
  ScheduleNewsletterData,
  CreateSectionData,
  UpdateSectionData,
  ReorderSectionsData,
  SendPreviewData,
  NewsletterFilters,
  PublicBlogFilters,
  PaginatedNewsletterResponse,
  PaginatedBlogResponse,
  RecipientListPreview,
  DistributionResult,
  NewsletterStats,
  NewsletterDetailedAnalytics,
} from '../types/newsletter.types';

const BASE_URL = '/newsletters';

// ===========================================
// API RESPONSE WRAPPER
// ===========================================

interface ApiResponse<T> {
  status: string;
  data: T;
  message?: string;
  meta?: {
    total: number;
  };
}

// ===========================================
// PUBLIC BLOG (No Auth Required)
// ===========================================

/**
 * Get public blog posts for a school
 */
export async function getPublicBlogPosts(
  schoolId: string,
  filters?: PublicBlogFilters
): Promise<PaginatedBlogResponse> {
  const response = await apiClient.get<ApiResponse<PublicBlogPost[]>>(
    `${BASE_URL}/public/blog`,
    {
      params: { schoolId, ...filters },
    }
  );
  return {
    data: response.data,
    total: response.meta?.total || response.data.length,
  };
}

/**
 * Get a single public blog post by slug
 */
export async function getPublicBlogPost(
  schoolId: string,
  slug: string
): Promise<PublicBlogPost> {
  const response = await apiClient.get<ApiResponse<PublicBlogPost>>(
    `${BASE_URL}/public/blog/${slug}`,
    { params: { schoolId } }
  );
  return response.data;
}

// ===========================================
// PORTAL NEWSLETTERS (Parent Access)
// ===========================================

/**
 * Get newsletters visible in the parent portal
 */
export async function getPortalNewsletters(
  filters?: NewsletterFilters
): Promise<PaginatedNewsletterResponse> {
  const response = await apiClient.get<ApiResponse<NewsletterListItem[]>>(
    `${BASE_URL}/portal`,
    { params: filters }
  );
  return {
    data: response.data,
    total: response.meta?.total || response.data.length,
  };
}

/**
 * Get a single portal newsletter by slug
 */
export async function getPortalNewsletter(slug: string): Promise<Newsletter> {
  const response = await apiClient.get<ApiResponse<Newsletter>>(
    `${BASE_URL}/portal/${slug}`
  );
  return response.data;
}

// ===========================================
// ADMIN NEWSLETTER CRUD
// ===========================================

/**
 * Create a new newsletter
 */
export async function createNewsletter(
  data: CreateNewsletterData
): Promise<Newsletter> {
  const response = await apiClient.post<ApiResponse<Newsletter>>(BASE_URL, data);
  return response.data;
}

/**
 * Get all newsletters with filters (admin view)
 */
export async function getNewsletters(
  filters?: NewsletterFilters
): Promise<PaginatedNewsletterResponse> {
  const response = await apiClient.get<ApiResponse<NewsletterListItem[]>>(
    BASE_URL,
    { params: filters }
  );
  return {
    data: response.data,
    total: response.meta?.total || response.data.length,
  };
}

/**
 * Get a single newsletter by ID
 */
export async function getNewsletter(newsletterId: string): Promise<Newsletter> {
  const response = await apiClient.get<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}`
  );
  return response.data;
}

/**
 * Update a newsletter
 */
export async function updateNewsletter(
  newsletterId: string,
  data: UpdateNewsletterData
): Promise<Newsletter> {
  const response = await apiClient.patch<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}`,
    data
  );
  return response.data;
}

/**
 * Delete a newsletter
 */
export async function deleteNewsletter(newsletterId: string): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${newsletterId}`);
}

/**
 * Duplicate a newsletter
 */
export async function duplicateNewsletter(
  newsletterId: string
): Promise<Newsletter> {
  const response = await apiClient.post<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}/duplicate`
  );
  return response.data;
}

// ===========================================
// PUBLISHING & SCHEDULING
// ===========================================

/**
 * Publish a newsletter immediately
 */
export async function publishNewsletter(
  newsletterId: string
): Promise<Newsletter> {
  const response = await apiClient.post<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}/publish`
  );
  return response.data;
}

/**
 * Schedule a newsletter for future publishing
 */
export async function scheduleNewsletter(
  newsletterId: string,
  data: ScheduleNewsletterData
): Promise<Newsletter> {
  const response = await apiClient.post<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}/schedule`,
    data
  );
  return response.data;
}

/**
 * Unpublish a newsletter (set to draft)
 */
export async function unpublishNewsletter(
  newsletterId: string
): Promise<Newsletter> {
  const response = await apiClient.post<ApiResponse<Newsletter>>(
    `${BASE_URL}/${newsletterId}/unpublish`
  );
  return response.data;
}

// ===========================================
// SECTION MANAGEMENT
// ===========================================

/**
 * Add a section to a newsletter
 */
export async function addSection(
  newsletterId: string,
  data: CreateSectionData
): Promise<NewsletterSection> {
  const response = await apiClient.post<ApiResponse<NewsletterSection>>(
    `${BASE_URL}/${newsletterId}/sections`,
    data
  );
  return response.data;
}

/**
 * Update a section
 */
export async function updateSection(
  newsletterId: string,
  sectionId: string,
  data: UpdateSectionData
): Promise<NewsletterSection> {
  const response = await apiClient.patch<ApiResponse<NewsletterSection>>(
    `${BASE_URL}/${newsletterId}/sections/${sectionId}`,
    data
  );
  return response.data;
}

/**
 * Delete a section
 */
export async function deleteSection(
  newsletterId: string,
  sectionId: string
): Promise<void> {
  await apiClient.delete(`${BASE_URL}/${newsletterId}/sections/${sectionId}`);
}

/**
 * Reorder sections
 */
export async function reorderSections(
  newsletterId: string,
  data: ReorderSectionsData
): Promise<NewsletterSection[]> {
  const response = await apiClient.put<ApiResponse<NewsletterSection[]>>(
    `${BASE_URL}/${newsletterId}/sections/reorder`,
    data
  );
  return response.data;
}

// ===========================================
// DISTRIBUTION
// ===========================================

/**
 * Get recipient list preview
 */
export async function getRecipientPreview(
  newsletterId: string
): Promise<RecipientListPreview> {
  const response = await apiClient.get<ApiResponse<RecipientListPreview>>(
    `${BASE_URL}/${newsletterId}/recipients`
  );
  return response.data;
}

/**
 * Send a preview email
 */
export async function sendPreviewEmail(
  newsletterId: string,
  data: SendPreviewData
): Promise<void> {
  await apiClient.post(`${BASE_URL}/${newsletterId}/preview`, data);
}

/**
 * Send newsletter to all recipients
 */
export async function sendNewsletter(
  newsletterId: string
): Promise<DistributionResult> {
  const response = await apiClient.post<ApiResponse<DistributionResult>>(
    `${BASE_URL}/${newsletterId}/send`
  );
  return response.data;
}

// ===========================================
// ANALYTICS
// ===========================================

/**
 * Get distribution analytics for a newsletter
 */
export async function getNewsletterStats(
  newsletterId: string
): Promise<NewsletterStats> {
  const response = await apiClient.get<ApiResponse<NewsletterStats>>(
    `${BASE_URL}/${newsletterId}/analytics`
  );
  return response.data;
}

/**
 * Get detailed analytics including top clicked URLs
 */
export async function getDetailedAnalytics(
  newsletterId: string
): Promise<NewsletterDetailedAnalytics> {
  const response = await apiClient.get<ApiResponse<NewsletterDetailedAnalytics>>(
    `${BASE_URL}/${newsletterId}/analytics/detailed`
  );
  return response.data;
}
